import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ContentBlocksView} from "./content/ContentBlocksView";
import {ChevronDown, Star} from "react-feather";
import {Pin, Summary} from "@webng/icons-common";
import EventSharing from "./event/EventSharing";
import {
  useCommentsMode,
  useEventSharingType,
  useLayoutAuthorPosition,
  useTrackingFunction
} from "./LiveblogRenderContext";
import {MultiMediaContentBlockViewExportedProps} from "./content/MultiMediaContentBlockView";
import {useMasonryProperties} from "./MasonryContainer";
import {NiceDateTimeHighlights} from "./NiceDateTimeHighlights";
import classNames from "classnames";
import {EventExtension} from "./extensions/Extension";
import {IEvent, IGame, ITag} from "@webng-types/write-model";
import {EventExtensionRenderer} from "./extensions/EventExtensionRenderer";
import {EventAuthor} from "./event/EventAuthor";
import {EventDateTime} from "./event/EventDateTime";
import {EventSportIcon} from "./event/EventSportIcon";
import useBreakpoints from "@webng/react-app-common/src/utils/useBreakpoints";
import {commentsEnabled} from "./converter/commentsEnabled";
import {isNativeCommentConfiguration, isViafouraCommentConfiguration} from "@webng/liveblog-edit-utils";
import {useTranslation} from "react-i18next";
import { TagImage } from "./TagImage";
import {isSummaryEvent} from "./utils/systemTags";
import {isHighlightedEvent, isStickyEvent} from "./utils/eventHighlight";
import {resolveEventBackground} from "./utils/eventBackground";
import EventReactions from "./event/EventReactions";
import {effectiveReactionSetId} from "@webng/liveblog-reactions";
import FoldInButton from "./content/components/FoldInButton";
import {useEnvironment} from "@webng/react-app-common";

export interface LiveblogEventRowExportedProps extends MultiMediaContentBlockViewExportedProps {
  readonly eventExtension?: EventExtension
}

export interface LiveblogEventRowProps extends LiveblogEventRowExportedProps {
  readonly game: IGame
  readonly event: IEvent
  /**
   * Suppresses the post's conversation entirely when false, for BOTH providers (native
   * comments and Viafoura). Defaults to true.
   *
   * Used by render surfaces where a conversation must not appear: Tickaroo-hosted share and
   * permalink pages, posts shown as a reference inside another post, and the editor's
   * moderation previews.
   *
   * For Viafoura it also keeps the widget off Tickaroo-hosted pages, which matters because Viafoura
   * resolves the account from the page's **hostname**: Tickaroo's own domains are deliberately not
   * provisioned with Viafoura (measured: `sections/sites/pro.tickaroo.com` → 404) and must stay that
   * way — were they registered, conversations on Tickaroo pages would resolve against *Tickaroo's*
   * Viafoura account rather than the publisher's. Surfaces that must keep native comments but cannot
   * host Viafoura use the render context's `viafouraInteractive` instead of this flag.
   */
  readonly enableComments?: boolean
  readonly isPreview?: boolean
  readonly reactionCounts?: {
    [reactionId: string]: number;
  }
  readonly isReference?: boolean
}

// const breakpoints: [string, number][] = [
//   ["sm", 480],
//   ["md", 1200],
//   ["lg", Infinity]
// ];

function mergeRefs<T = any>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

function useTags(definedTags: ITag[]|undefined, eventTagIds: string[]|undefined): ITag[] {
  return useMemo(() => {
    if(eventTagIds && definedTags) {
      return eventTagIds.map(tag_id => {
        return definedTags.find(t => t._id === tag_id) || null
      }).filter(tag => tag !== null) as ITag[]
    }
    else {
      return []
    }
  }, [definedTags, eventTagIds])
}

export function LiveblogEventRow({game, event, eventExtension, isPreview, reactionCounts, enableComments = true, ...props}: LiveblogEventRowProps)  {
  const {ref, css} = useMasonryProperties()
  const {t} = useTranslation("liveblog")
  const trackingFunction = useTrackingFunction();

  const [isHeightLimitActive, setIsHeightLimitActive] = useState(true) // height limit
  const [isBreakpointActive, setIsBreakpointActive] = useState(true) // breakpoint

  const toggleHeightLimit = useCallback(() => {
    trackingFunction({t: "a_ehl", e: event.local_id})
    setIsHeightLimitActive(isActive => !isActive)
  }, [event.local_id, trackingFunction])

  const toggleBreakpoint = useCallback(() => {
    trackingFunction({t: "a_ebp", e: event.local_id})
    setIsBreakpointActive(isActive => !isActive)
  }, [event.local_id, trackingFunction])

  // reset foldout state whenever the foldout flag changes
  useEffect(() => {
    setIsHeightLimitActive(true)
  }, [event.fold_out])

  useEffect(() => {
    setIsBreakpointActive(true)
  }, [event.fold_out_index])

  const visibleTags = useTags(game.event_tags?.filter(tag => tag.show_in_event ?? true)?.sort((a,b) => a.name.localeCompare(b.name)), event.tag_ids)
  const allTags= useTags(game.event_tags?.sort((a,b) => a.name.localeCompare(b.name)), event.tag_ids)
  const editor = useMemo(() => {
    if(event.editor ) return event.editor ;

    // if no editor is set, use the usual lookup by user_id, but prefer the original fields over the `local_*` fields
    const fallbackEditor = game.editors?.find(e => e._id === event.user_id)
    if(fallbackEditor) {
      return fallbackEditor
    }

    return undefined
  }, [event.editor, event.user_id, game.editors])

  const isHighlightEvent = isHighlightedEvent(event)
  const isPinnedEvent = isStickyEvent(event)

  const isSummary = isSummaryEvent(event);
  const eventSharingType = useEventSharingType();
  const commentsMode = useCommentsMode();
  const layoutAuthorPosition = useLayoutAuthorPosition();

  const conversationVisible = enableComments
    && (commentsMode === "default" || commentsMode === "inline")
    && commentsEnabled(event.comments);
  const {ref: sizeRef, breakpoint: breakpointClassName} = useBreakpoints([
    ["", 460],
    ["tik4-event--xs", 576],
    ["tik4-event--xs tik4-event--sm", 768],
    ["tik4-event--xs tik4-event--sm tik4-event--md", 992],
    ["tik4-event--xs tik4-event--sm tik4-event--md tik4-event--lg", 1200],
    ["tik4-event--xs tik4-event--sm tik4-event--md tik4-event--lg tik4-event--xl", Infinity]
  ]);


  let eventHighlightClassName = '';
  if (isPinnedEvent) {
    eventHighlightClassName += ' tik4-event--pin';
  }
  if (isHighlightEvent) {
    eventHighlightClassName += ' tik4-event--highlight';
  }
  //TODO:Timo check class name
  if (isSummary) {
    eventHighlightClassName += ' tik4-event--summary';
  }

  const isCancelled = event.sport_event && event.sport_event.is_cancelled;

  const environment = useEnvironment();
  const resolvedBackground = useMemo(() => resolveEventBackground(event, environment.apiHost), [event, environment.apiHost]);


  // this class has no styles, is only used so a meaningful change occurs when configuration changes for
  // height recalculations
  const authorClassNames = classNames(
    "tik4-author",
    {
      "tik4-author--author-top": layoutAuthorPosition === "top",
      "tik4-author--author-bottom-left": layoutAuthorPosition === "bottomLeft",
      "tik4-author--author-bottom-right": layoutAuthorPosition === "bottomRight",
      "tik4-author--author-off": layoutAuthorPosition === "off",
    }
  )

  const eventClassNames = classNames(
    "tik4-event",
    {
      [`${breakpointClassName}`]: breakpointClassName,
      "tik4-event--sport": event.sport_event,
      [`tik4-event--sport--${event.sport_event?.event_type?.replace(".", "-")}`]: event.sport_event?.event_type,
      [`${eventHighlightClassName}`]: eventHighlightClassName,
      "tik4-event--cancelled": isCancelled,
      "tik4-event--hlim": event.fold_out && isHeightLimitActive, // heightlimit hlim
      "tik4-event--hlimi": event.fold_out_index, // breakpoint hlim
      "tik4-event--hlimi-hidden":  event.fold_out_index && isBreakpointActive, // breakpoint hlim
      "tik4-event--hlimi-visible": event.fold_out_index && !isBreakpointActive, // breakpoint hlim
      "tik4-event--bg": resolvedBackground,
      [`${resolvedBackground?.textStyleClassName}`]: resolvedBackground,
    [`${allTags.map((tag) => `tik4-event--tag-${tag.slug || 'no-id'}`).join(" ")}`]: allTags.length > 0
    }
  )

  const eventStyle = useMemo(() => {
    if(resolvedBackground) {
      // Tiered (image): --t4-ev-cbg is left unset here so the per-breakpoint CSS rules
      // in tik4-event.css (each picking its own --t4-ev-cbg-<tier> variable) control it —
      // setting it inline would always win over those rules and defeat tier selection.
      // Color-only ("none"): set --t4-ev-cbg to "none" directly so the gradient layer
      // (--t4-ev-scrim) is the only bg layer, regardless of breakpoint.
      return {
        ...css,
        ...(resolvedBackground.isTiered ? {} : {"--t4-ev-cbg": resolvedBackground.backgroundValue}),
        ...resolvedBackground.cssVarOverrides
      } as React.CSSProperties
    } else {
      return css
    }
  }, [css, resolvedBackground])

  const reactionSetId = effectiveReactionSetId(game, event);
  const showReactions = game.owner?._id && !!reactionSetId && !props.isReference;
  const showEventFooter = showReactions;

  const backgroundCredit = event.background?.image?.credit;
  const showBackgroundCredit = !!backgroundCredit;


  return <div ref={mergeRefs([ref,sizeRef])} style={eventStyle} className={eventClassNames}
              {...(!props.isReference ? {'data-tickaroo-event-id': event.local_id} : {})}>
    <EventExtensionRenderer event={event} Extension={eventExtension?.EventTop}/>
    <div className="tik4-event__timeline"/>
    <div className="tik4-event__body">

      {(game.use_event_tags) && visibleTags.length > 0 &&
        <div className="tik4-tags">
          {visibleTags.map((tag) => {
              return <div key={tag._id} className={`tik4-tag tik4-tag--${tag.slug || 'no-id'}`}>
                <TagImage image={tag.image} className="tik4-tag__img" />
                <div className="tik4-tag__name">{tag.name}</div>
              </div>
            }
          )}
        </div>
      }
      <div className="tik4-meta">
        <div className="tik4-meta__timeline">
          { event.sport_event && event.sport_event.event_type &&
            <div className="tik4-meta__timeline__si">
              <EventSportIcon className="tik4-icon tik4-icon--sport tik4-meta__timeline__si__i" block={event.sport_event} />
            </div>
          }
          {(isHighlightEvent || isPinnedEvent || isSummary) &&
            <div className="tik4-meta__timeline__svg-icon-container">
              {isHighlightEvent && <Star className="tik4-icon tik4-icon--star tik4-meta__timeline__svg-icon"/>}
              {isPinnedEvent && <Pin className="tik4-icon tik4-icon--pin tik4-meta__timeline__svg-icon"/>}
              {isSummary && <Summary className="tik4-icon tik4-icon--summary tik4-meta__timeline__svg-icon"/>}
            </div>
          }
          <div className="tik4-meta__timeline__dot">
          </div>
        </div>

        <EventDateTime event={event}>
          <React.Fragment>
            <NiceDateTimeHighlights isHighlightEvent={isHighlightEvent} isStickyEvent={isPinnedEvent} sportEvent={event.sport_event} isSummary={isSummary} />
            {isSummary && <span className="tik4-meta__ts__time__title"><span className="tik4-meta__ts__time__title__c">{t("liveblog.event.summary.title")}</span></span>}
          </React.Fragment>
        </EventDateTime>

      </div>

      {editor && <EventAuthor className={authorClassNames} editor={editor} showByline={true} isFallback={!event.editor}/>}

      <ContentBlocksView
        game={game}
        event={event}
        eventExtension={eventExtension}
        comments={conversationVisible && isNativeCommentConfiguration(event.comments) ? ({
          version: event.version!,
          channelId: event.local_id!,
          ownerId: game.owner!._id!,
          displayMode: commentsMode === "inline" ? "inline" : "modal"
        }) : undefined}
        /* Viafoura always uses the overlay: `inline` would mean one live widget per post, and
           Viafoura only supports a single shared container anyway (design D15). */
        viafouraConversation={conversationVisible && isViafouraCommentConfiguration(event.comments) ? ({
          containerId: event.local_id!
        }) : undefined}
        isPreview={isPreview}
        toggleBreakpoint={toggleBreakpoint}
        isBreakpointActive={isBreakpointActive}
        {...props} />

      {showEventFooter &&
        <div className="tik4-ef">
          {/* ef: event footer */}
          <div className="tik4-ef__l">
            {/* ef__l: event footer left */}
          </div>
          <div className="tik4-ef__r">
            {showReactions && <>
              {/* ef__r: event footer right */}
              <EventReactions
                reactionSetId={reactionSetId!}
                organizationId={game.owner!._id!}
                liveblogId={game.local_id!}
                eventId={event.local_id!}
                reactionCounts={reactionCounts}
                eventRef={ref}
              />
            </>
            }
          </div>
        </div>
      }
      {eventSharingType !== "off" && <EventSharing eventId={event.local_id!} type={eventSharingType}/>}
      {showBackgroundCredit &&
        <div className="tik4-bg-credit">{backgroundCredit}</div>
      }
    </div>

      {/* heightlimit hlim */}
    {(event.fold_out && isHeightLimitActive) &&
      <div className="tik4-hlim">
        <button className="tik4-hlim__link tik4-link" onClick={toggleHeightLimit}>
          <ChevronDown className="tik4-icon tik4-icon--mr"/>
          <span className="tik4-hlim__link__text">
            {t('liveblog.event.fold_out.button')}
          </span>
        </button>
      </div>
    }
     {/* TODO:Timo tik4-hlimi is hidden, when the breakpoint is not active, but contains the styling for the button
    I'm setting the style here to override that for now, please fix in CSS */}
    {(event.fold_out && !isHeightLimitActive) &&
      <FoldInButton onClick={toggleHeightLimit} className="tik4-fold-in--hlim" />
    }

    <EventExtensionRenderer event={event} Extension={eventExtension?.EventBottom}/>
  </div>;
}
